import { useAtom, atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useEnableFormAutoOpen } from '../../hooks/useEnableFormAutoOpen';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle,
} from '@rocco/components/dialog';
import { Drawer, DrawerContent } from '@rocco/components/drawer';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';
import { ReactComponent as LogoIcon } from '@rocco/icons/svg/archipro-symbol-logo.svg';
import { ReactComponent as HDELogo } from '../icons/hde-logo.svg';
import { ReactComponent as HDEHomeDesignEvening } from '../icons/hde-home-design-evening.svg';
import { HDERegistrationForm } from './HDERegistrationForm';
import { HDEInitialFlow } from './HDEInitialFlow';
import { ProfessionalRegistrationForm } from './ProfessionalRegistrationForm';
import { ExhibitorRegistrationForm } from './ExhibitorRegistrationForm';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import { BUILD_HUB_STAGE_CONFIG_API_ROUTE } from '~/routes/remix-api.build-hub.stage-config';
import type { LocationOption } from './types';
import { useHDELocationOptions } from '../../hooks/useHDELocationOptions';
import { useHDEEventDisplayInfo } from '../../hooks/useHDEEventDisplayInfo';

type ApplicationType = 'Homeowner' | 'Professional' | 'Exhibitor';

export const hdeRegistrationModalIsOpenAtom = atom(false);
export const hdeRegistrationModalLocationOptionsAtom = atom<LocationOption[]>(
    [],
);

interface HDERegistrationModalProps {
    /** Page modules used to derive location options when opening from URL (e.g. after social login redirect) */
    modules?: unknown[];
}

export function HDERegistrationModal({
    modules = [],
}: HDERegistrationModalProps) {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const [isOpen, setIsOpen] = useAtom(hdeRegistrationModalIsOpenAtom);
    const setLocationOptions = useSetAtom(
        hdeRegistrationModalLocationOptionsAtom,
    );
    const locationOptionsFromModules = useHDELocationOptions(modules);
    const { dateDisplay, locationDisplay } = useHDEEventDisplayInfo(modules);

    const [socialLoginSubmitted, setSocialLoginSubmitted] = useState(false);
    const [socialLoginResumed, setSocialLoginResumed] = useState(false);
    const [socialLoginEventId, setSocialLoginEventId] = useState<string | null>(
        null,
    );
    const [formKey, setFormKey] = useState(0);
    const [applicationType, setApplicationType] =
        useState<ApplicationType | null>(null);
    const [returnedFromSubForm, setReturnedFromSubForm] = useState(false);
    /** Hides the existing-submission gate once the member has resolved it (e.g. chose
     *  "continue as my existing type", which switches the sub-form). */
    const [existingGateResolved, setExistingGateResolved] = useState(false);

    useEnableFormAutoOpen({
        setIsOpen,
        setSocialLoginSubmitted,
        setSocialLoginResumed,
        setLocationOptions,
        locationOptionsFromModules,
        setSocialLoginEventId,
        setApplicationType,
    });

    const locationOptionsFromAtom = useAtomValue(
        hdeRegistrationModalLocationOptionsAtom,
    );
    const locationOptions =
        locationOptionsFromAtom.length > 0
            ? locationOptionsFromAtom
            : locationOptionsFromModules;

    const [stageConfig, setStageConfig] = useState<StageConfiguration | null>(
        null,
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentStepId, setCurrentStepId] = useState<string>('');
    const [showEmailForm, setShowEmailForm] = useState(false);

    const isAuthStep = currentStepId === 'authentication';
    const isAuthInitialView = isAuthStep && !showEmailForm;

    useEffect(() => {
        const img = new Image();
        img.src = '/assets/website/ui/images/auth-side-bg.png';
    }, []);

    useEffect(() => {
        if (isOpen && !stageConfig) {
            setLoading(true);
            fetch(BUILD_HUB_STAGE_CONFIG_API_ROUTE)
                .then((res) => {
                    if (!res.ok)
                        throw new Error('Failed to load configuration');
                    return res.json();
                })
                .then((data) => {
                    setStageConfig(data);
                    setLoading(false);
                })
                .catch((err) => {
                    setError(err.message);
                    setLoading(false);
                });
        }
    }, [isOpen, stageConfig]);

    const resetFormAndClose = () => {
        setSocialLoginSubmitted(false);
        setSocialLoginResumed(false);
        setSocialLoginEventId(null);
        setCurrentStepId('');
        setShowEmailForm(false);
        setApplicationType(null);
        setReturnedFromSubForm(false);
        setExistingGateResolved(false);
        setFormKey((k) => k + 1);
    };

    const handleBackToApplicationType = () => {
        setApplicationType(null);
        setReturnedFromSubForm(true);
        setExistingGateResolved(false);
        setFormKey((k) => k + 1);
    };

    const handleApplicationTypeSelected = (type: ApplicationType) => {
        setApplicationType(type);
        // A fresh manual pick re-evaluates the gate (don't carry a prior resolution).
        setExistingGateResolved(false);
        setFormKey((k) => k + 1);
    };

    /** Keep the member's existing registration by switching the modal to its type. */
    const handleSwitchApplicationType = (type: ApplicationType) => {
        setApplicationType(type);
        setExistingGateResolved(true);
        setFormKey((k) => k + 1);
    };

    const handleClose = () => {
        setIsOpen(false);
        resetFormAndClose();
    };

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (!open) {
            resetFormAndClose();
        }
    };

    const formContent = (
        <div className="flex min-h-0 max-w-[100vw] flex-1 flex-col overflow-hidden rounded-br-[0.70369rem] rounded-tr-[0.70369rem] bg-[#EAEAEA] md:w-[29.67rem] md:flex-none md:rounded-r-[0.70369rem]">
            <DialogTitle className="sr-only">
                Register for Home Design Evening
            </DialogTitle>
            <DialogDescription className="sr-only">
                Complete registration for ArchiPro Home Design Evening
            </DialogDescription>
            {loading && (
                <div className="flex flex-1 items-center justify-center bg-[#EAEAEA]">
                    <div className="text-[#2A2A2A]/70">
                        {socialLoginSubmitted
                            ? 'Submitting your registration...'
                            : 'Loading...'}
                    </div>
                </div>
            )}

            {error && (
                <div className="flex flex-1 items-center justify-center bg-[#EAEAEA]">
                    <div
                        className={cn(
                            (isAuthStep || !currentStepId) && 'text-red-600',
                            !isAuthStep && currentStepId && 'text-red-500',
                        )}
                    >
                        {error}
                    </div>
                </div>
            )}

            {!loading && !error && stageConfig && (
                <>
                    {applicationType === null ? (
                        <HDEInitialFlow
                            key={formKey}
                            stageConfig={stageConfig}
                            locationOptions={locationOptions}
                            layout="split"
                            onClose={handleClose}
                            onStepChange={setCurrentStepId}
                            onApplicationTypeSelected={
                                handleApplicationTypeSelected
                            }
                            startAtApplicationType={returnedFromSubForm}
                        />
                    ) : applicationType === 'Homeowner' ? (
                        <HDERegistrationForm
                            key={formKey}
                            stageConfig={stageConfig}
                            locationOptions={locationOptions}
                            layout="split"
                            onClose={handleClose}
                            onStepChange={setCurrentStepId}
                            onAuthViewChange={setShowEmailForm}
                            socialLoginSubmitted={socialLoginSubmitted}
                            socialLoginEventId={socialLoginEventId}
                            onBackToApplicationType={
                                handleBackToApplicationType
                            }
                            onSwitchApplicationType={
                                handleSwitchApplicationType
                            }
                            suppressExistingSubmissionGate={
                                existingGateResolved
                            }
                            onExistingSubmissionGateResolved={() =>
                                setExistingGateResolved(true)
                            }
                        />
                    ) : applicationType === 'Professional' ? (
                        <ProfessionalRegistrationForm
                            key={formKey}
                            locationOptions={locationOptions}
                            layout="split"
                            onClose={handleClose}
                            onStepChange={setCurrentStepId}
                            onAuthViewChange={setShowEmailForm}
                            socialLoginSubmitted={socialLoginSubmitted}
                            socialLoginResumed={socialLoginResumed}
                            socialLoginEventId={socialLoginEventId}
                            onBackToApplicationType={
                                handleBackToApplicationType
                            }
                            onSwitchApplicationType={
                                handleSwitchApplicationType
                            }
                            suppressExistingSubmissionGate={
                                existingGateResolved
                            }
                            onExistingSubmissionGateResolved={() =>
                                setExistingGateResolved(true)
                            }
                        />
                    ) : (
                        <ExhibitorRegistrationForm
                            key={formKey}
                            locationOptions={locationOptions}
                            layout="split"
                            onClose={handleClose}
                            onStepChange={setCurrentStepId}
                            onAuthViewChange={setShowEmailForm}
                            socialLoginSubmitted={socialLoginSubmitted}
                            socialLoginResumed={socialLoginResumed}
                            socialLoginEventId={socialLoginEventId}
                            onBackToApplicationType={
                                handleBackToApplicationType
                            }
                            onSwitchApplicationType={
                                handleSwitchApplicationType
                            }
                            suppressExistingSubmissionGate={
                                existingGateResolved
                            }
                            onExistingSubmissionGateResolved={() =>
                                setExistingGateResolved(true)
                            }
                        />
                    )}
                </>
            )}
        </div>
    );

    if (isMobile) {
        return (
            <Drawer
                open={isOpen}
                onOpenChange={handleOpenChange}
                direction="bottom"
            >
                <DrawerContent
                    variant="default"
                    overlayVariant="dark"
                    noHandle={true}
                    limitMaxHeight={false}
                    closeButtonClassName="right-4! top-3! z-50! flex! size-[1.04675rem]! items-center! justify-center! gap-[0.17rem]! rounded-[17.664rem]! border-0! bg-[#C8C8C8]! opacity-100! shadow-none! outline-hidden! ring-0! ring-offset-0! focus:ring-0! focus:ring-offset-0!"
                    closeButtonIconClassName="size-3!"
                    className="data-[vaul-drawer-direction=bottom]:h-[calc(100vh-6rem)] data-[vaul-drawer-direction=bottom]:max-h-[calc(100vh-5rem)] data-[vaul-drawer-direction=bottom]:overflow-hidden data-[vaul-drawer-direction=bottom]:rounded-t-[0.70369rem] data-[vaul-drawer-direction=bottom]:bg-[#EAEAEA]"
                >
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        {formContent}
                    </div>
                </DrawerContent>
            </Drawer>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent
                variant="hde"
                className={cn(
                    'flex flex-col overflow-hidden rounded-[0.70369rem] border-0 p-0',
                    'left-1/2 top-1/2 h-[90vh] max-h-[90vh] w-[95vw] max-w-[95vw] -translate-x-1/2 -translate-y-1/2',
                    'md:h-[56.25rem] md:w-[59.34556rem] md:max-w-[59.34556rem]',
                    isAuthInitialView && '[transition:none]',
                    'bg-transparent!',
                )}
                closeButton={true}
                closeButtonVariant="roundedSmall"
                closeButtonClassName="right-4! top-4! z-50! size-5! border-0! outline-hidden! ring-0! ring-offset-0! focus:ring-0! focus:ring-offset-0! shadow-none! bg-[#FDFDFD]! text-[#2A2A2A]! md:hover:bg-[#E5E5E5]!"
            >
                <div className="flex min-h-0 min-w-full flex-1 flex-col overflow-hidden md:flex-row">
                    <div
                        className="relative hidden overflow-hidden md:flex md:w-[29.67rem] md:rounded-l-[0.70369rem]"
                        style={{
                            backgroundImage:
                                'url(/assets/website/ui/images/auth-side-bg.png)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundColor: '#0a0a0a',
                        }}
                    >
                        <div className="absolute left-7 top-20 z-10 flex items-center justify-center">
                            <HDELogo className="h-auto md:w-73" />
                        </div>

                        <div className="absolute bottom-60 right-12 z-10">
                            <HDEHomeDesignEvening className="h-auto w-[14rem] max-w-full" />
                        </div>

                        <div className="absolute inset-x-6 bottom-32 z-10 flex flex-col gap-2.5">
                            <p className="w-68 text-[0.7rem] uppercase leading-[90%] text-[#eee]">
                                Register for the event and unlock the full
                                ArchiPro experience — design boards, product
                                research, and direct access to NZ&apos;s top
                                professionals.
                            </p>
                        </div>
                        <div className="absolute bottom-9 left-6 flex h-25 items-center">
                            <div className="flex items-center gap-1.5 text-[0.62rem] font-medium uppercase tracking-wide text-[#eee]">
                                {dateDisplay && <span>{dateDisplay}</span>}
                                {dateDisplay && locationDisplay && (
                                    <span>|</span>
                                )}
                                {locationDisplay && (
                                    <span>{locationDisplay}</span>
                                )}
                            </div>
                        </div>
                        <span className="absolute bottom-20 right-32 text-[0.62rem] font-medium uppercase tracking-wide text-[#eee]">
                            BY ARCHIPRO
                        </span>
                        <LogoIcon className="absolute bottom-16 right-6 h-auto w-25 fill-white" />
                    </div>
                    <div className="absolute left-0 top-0 z-0 hidden h-12 w-full border-b border-dashed border-gray-500 md:block">
                        <div className="absolute right-6 top-6 h-[0.625rem] w-[0.625rem] rounded-full bg-white"></div>
                        <div className="absolute left-6 top-6 h-[0.625rem] w-[0.625rem] rounded-full bg-white"></div>
                    </div>
                    {formContent}
                    <div className="absolute bottom-0 left-0 z-modal hidden h-12 w-full border-t border-dashed border-gray-500 md:block">
                        <div className="absolute bottom-6 right-6 h-[0.625rem] w-[0.625rem] rounded-full bg-white"></div>
                        <div className="absolute bottom-6 left-6 h-[0.625rem] w-[0.625rem] rounded-full bg-white"></div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
